"use client";

import * as React from "react";
import {
  Check,
  Copy,
  Landmark,
  Loader2,
  Upload,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { faToman, formatCard, toFa } from "@/lib/format";

export type Plan = {
  id: string;
  name: string;
  priceToman: number;
  durationDays: number;
  trafficGB: number;
};

type BankCard = {
  id: string;
  bankName: string;
  cardNumber: string;
  holderName: string;
};

export function BuyPlanFlow({
  plan,
  onClose,
  onSubmitted,
}: {
  plan: Plan;
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const [cards, setCards] = React.useState<BankCard[] | null>(null);
  const [senderCard, setSenderCard] = React.useState("");
  const [trackingCode, setTrackingCode] = React.useState("");
  const [note, setNote] = React.useState("");
  const [receiptPath, setReceiptPath] = React.useState<string | null>(null);
  const [uploading, setUploading] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [copiedId, setCopiedId] = React.useState<string | null>(null);
  const [touched, setTouched] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    fetch("/api/bank-cards", { cache: "no-store" })
      .then((r) => r.json())
      .then((d: { cards?: BankCard[] }) => {
        if (alive) setCards(d.cards ?? []);
      })
      .catch(() => {
        if (alive) setCards([]);
        toast.error("بارگذاری کارت‌های بانکی ناموفق بود");
      });
    return () => {
      alive = false;
    };
  }, []);

  const senderDigits = senderCard.replace(/\D/g, "");
  const senderValid = senderDigits.length === 16;
  const senderError = touched && !senderValid;

  const copyCard = async (card: BankCard) => {
    try {
      await navigator.clipboard.writeText(card.cardNumber);
      setCopiedId(card.id);
      toast.success("شماره کارت کپی شد!");
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      toast.error("کپی ناموفق بود");
    }
  };

  const onFileSelected = async (file: File | undefined) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("حداکثر حجم فایل ۵ مگابایت است");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch("/api/upload", { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok || !data.path) {
        throw new Error(data.error || "آپلود ناموفق بود");
      }
      setReceiptPath(data.path);
      toast.success("تصویر آپلود شد");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "آپلود ناموفق بود";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async () => {
    setTouched(true);
    if (!senderValid) {
      toast.error("شماره کارت فرستنده باید ۱۶ رقم باشد");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/payments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          planId: plan.id,
          senderCard: senderDigits,
          trackingCode: trackingCode.trim() || undefined,
          note: note.trim() || undefined,
          receiptImage: receiptPath || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "ثبت پرداخت ناموفق بود");
      toast.success("درخواست پرداخت ثبت شد. پس از تأیید مدیر فعال می‌شود.");
      onSubmitted();
      onClose();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "ثبت پرداخت ناموفق بود";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="scrollbar-thin max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>خرید طرح {plan.name}</DialogTitle>
          <DialogDescription>
            مراحل پرداخت کارت به کارت را تکمیل کنید.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 text-center">
          <p className="text-xs text-muted-foreground">مبلغ قابل پرداخت</p>
          <p className="mt-0.5 text-lg font-extrabold text-primary">
            {faToman(plan.priceToman)}
          </p>
        </div>

        {/* Step 1 — bank cards */}
        <section className="space-y-3" aria-label="کارت‌های بانکی">
          <div className="flex items-center gap-2">
            <span className="flex size-6 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
              ۱
            </span>
            <h4 className="text-sm font-semibold">
              به یکی از کارت‌های زیر واریز کنید
            </h4>
          </div>
          {cards === null ? (
            <div className="space-y-2">
              <Skeleton className="h-20 w-full rounded-xl" />
              <Skeleton className="h-20 w-full rounded-xl" />
            </div>
          ) : cards.length === 0 ? (
            <p className="rounded-xl border bg-muted/20 p-3 text-xs text-muted-foreground">
              کارت بانکی موجود نیست. لطفاً بعداً تلاش کنید.
            </p>
          ) : (
            <div className="space-y-2">
              {cards.map((card) => (
                <div
                  key={card.id}
                  className="flex flex-col gap-2 rounded-xl border bg-muted/30 p-3"
                >
                  <div className="flex items-center gap-2">
                    <span className="flex size-8 items-center justify-center rounded-lg bg-primary/10">
                      <Landmark className="size-4 text-primary" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">
                        بانک {card.bankName}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {card.holderName}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <code
                      dir="ltr"
                      className="font-mono text-sm tracking-wider"
                    >
                      {formatCard(card.cardNumber)}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5"
                      onClick={() => copyCard(card)}
                    >
                      {copiedId === card.id ? (
                        <Check className="size-3.5 text-primary" />
                      ) : (
                        <Copy className="size-3.5" />
                      )}
                      {copiedId === card.id ? "کپی شد" : "کپی شماره کارت"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <Separator />

        {/* Step 2 — payment form */}
        <section className="space-y-4" aria-label="اطلاعات پرداخت">
          <div className="flex items-center gap-2">
            <span className="flex size-6 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
              ۲
            </span>
            <h4 className="text-sm font-semibold">اطلاعات پرداخت را وارد کنید</h4>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="sender-card">شماره کارت فرستنده</Label>
            <Input
              id="sender-card"
              dir="ltr"
              inputMode="numeric"
              placeholder="1234 5678 9012 3456"
              value={senderCard}
              onChange={(e) =>
                setSenderCard(e.target.value.replace(/[^\d]/g, "").slice(0, 16))
              }
              onBlur={() => setTouched(true)}
              aria-invalid={senderError}
              disabled={submitting}
              className="h-10 rounded-xl text-left font-mono"
            />
            <p
              className={cn(
                "text-xs",
                senderError ? "text-destructive" : "text-muted-foreground",
              )}
            >
              {senderError
                ? "شماره کارت باید ۱۶ رقم باشد"
                : `${toFa(senderDigits.length)} / ۱۶ رقم`}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="tracking-code">کد رهگیری (اختیاری)</Label>
            <Input
              id="tracking-code"
              dir="ltr"
              inputMode="numeric"
              placeholder="کد رهگیری بانکی"
              value={trackingCode}
              onChange={(e) => setTrackingCode(e.target.value)}
              disabled={submitting}
              className="h-10 rounded-xl text-left"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="note">توضیحات (اختیاری)</Label>
            <Textarea
              id="note"
              rows={2}
              placeholder="در صورت نیاز توضیحات خود را وارد کنید"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={submitting}
              className="rounded-xl"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="receipt">تصویر فیش واریزی</Label>
            {receiptPath ? (
              <div className="flex items-center gap-3 rounded-xl border bg-muted/30 p-2">
                <img
                  src={receiptPath}
                  alt="پیش‌نمایش فیش"
                  className="size-14 rounded-lg object-cover"
                />
                <div className="flex-1 text-xs text-muted-foreground">
                  تصویر فیش آپلود شد
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => setReceiptPath(null)}
                  disabled={uploading || submitting}
                  aria-label="حذف تصویر فیش"
                >
                  <X className="size-4" />
                </Button>
              </div>
            ) : (
              <label
                htmlFor="receipt"
                className={cn(
                  "flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed bg-muted/20 px-3 py-6 text-center transition hover:bg-muted/40",
                  (uploading || submitting) && "opacity-70",
                )}
              >
                {uploading ? (
                  <Loader2 className="size-5 animate-spin text-primary" />
                ) : (
                  <Upload className="size-5 text-primary" />
                )}
                <span className="text-xs text-muted-foreground">
                  {uploading ? "در حال آپلود..." : "برای انتخاب تصویر کلیک کنید"}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  jpg / png / webp — حداکثر ۵ مگابایت
                </span>
                <input
                  id="receipt"
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  disabled={uploading || submitting}
                  onChange={(e) => onFileSelected(e.target.files?.[0])}
                />
              </label>
            )}
            <p className="text-[11px] text-muted-foreground">
              می‌توانید بعداً فیش را آپلود کنید.
            </p>
          </div>
        </section>

        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
          >
            انصراف
          </Button>
          <Button
            onClick={onSubmit}
            disabled={submitting || uploading}
            className="gap-2"
          >
            {submitting ? <Loader2 className="size-4 animate-spin" /> : null}
            {submitting ? "در حال ثبت..." : "ثبت درخواست پرداخت"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default BuyPlanFlow;
